import { useLocation, matchPath } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'

const ROUTES = [
  { pattern: '/bp-list/:id/:folder/*',    contextType: 'bp',        idParam: 'id', folderParam: 'folder' },
  { pattern: '/bp-list/:id/:folder',      contextType: 'bp',        idParam: 'id', folderParam: 'folder' },
  { pattern: '/bp-list/:id',              contextType: 'bp',        idParam: 'id' },
  { pattern: '/drafts/:id/:folder/*',     contextType: 'bp',        idParam: 'id', folderParam: 'folder' },
  { pattern: '/drafts/:id/:folder',       contextType: 'bp',        idParam: 'id', folderParam: 'folder' },
  { pattern: '/drafts/:id',               contextType: 'bp',        idParam: 'id' },
  { pattern: '/favourites/:id/:folder/*', contextType: 'bp',        idParam: 'id', folderParam: 'folder' },
  { pattern: '/favourites/:id/:folder',   contextType: 'bp',        idParam: 'id', folderParam: 'folder' },
  { pattern: '/favourites/:id',           contextType: 'bp',        idParam: 'id' },
  { pattern: '/portfolios/:id/*',         contextType: 'portfolio', idParam: 'id' },
  { pattern: '/portfolios/:id',           contextType: 'portfolio', idParam: 'id' },
  { pattern: '/estates/:id/*',            contextType: 'estate',    idParam: 'id' },
  { pattern: '/estates/:id',              contextType: 'estate',    idParam: 'id' },
]

const KNOWN_BP_FOLDERS = new Set([
  'wiki', 'other', 'compliance', 'units', 'notes', 'inbox', 'accessControl',
  'guru', 'reporting', 'task-reports', 'analytics-template', 'file-versions',
  'emails', 'access-requests', 'notice-board', 'overview',
])

// Top-level hub pages: /tasks, /actions, /incidents, /calendar.
// They use ?bp=<id> or ?portfolio=<id> for scope and ?tab=… for sub-view.
// When a hub page carries one of those query params we promote it to a real
// BP / portfolio context so the launcher and recommendations work the same
// as on the detail pages. With no scope param the launcher stays hidden —
// there's no aggregate context to query yet.
const HUB_ROUTES = [
  { pattern: '/tasks/*',     surfacePrefix: 'tasks' },
  { pattern: '/tasks',       surfacePrefix: 'tasks' },
  { pattern: '/actions/*',   surfacePrefix: 'actions' },
  { pattern: '/actions',     surfacePrefix: 'actions' },
  { pattern: '/incidents/*', surfacePrefix: 'incidents' },
  { pattern: '/incidents',   surfacePrefix: 'incidents' },
  { pattern: '/calendar/*',  surfacePrefix: 'calendar' },
  { pattern: '/calendar',    surfacePrefix: 'calendar' },
]

const KNOWN_HUB_TABS = new Set([
  'tasks', 'actions', 'incidents', 'overview', 'compliance', 'calendar', 'reports',
])

// Entity-id detection within the BP detail route. Returns { entityType, entityId }.
// Patterns: /bp-list/:id/source/:fileId, /bp-list/:id/units/:unitId, etc.
// Top-level pages (/tasks, /actions, /incidents) reserve some sub-paths for
// non-entity views (create, drafts, etc.) — RESERVED_SEGMENTS keeps those out
// of entity detection so `/tasks/create` doesn't get treated as a task UUID.
const ENTITY_PATTERNS = [
  { folder: 'source',  pattern: '/bp-list/:bpId/source/:entityId',          entityType: 'file' },
  { folder: 'units',   pattern: '/bp-list/:bpId/units/:entityId',           entityType: 'unit' },
  { folder: 'members', pattern: '/bp-list/:bpId/members/:entityId',         entityType: 'member' },
  // Top-level task detail pages — emit entity even though the URL isn't BP-scoped;
  // the recommendation widget is hidden on these routes today, but the entity
  // value will be picked up if/when we lift visibility.
  { pattern: '/tasks/:entityId',     entityType: 'task' },
  { pattern: '/actions/:entityId',   entityType: 'action' },
  { pattern: '/incidents/:entityId', entityType: 'incident' },
]

const RESERVED_SEGMENTS = new Set(['create', 'drafts', 'new', 'edit', 'subscribe'])

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function detectEntity(pathname) {
  for (const candidate of ENTITY_PATTERNS) {
    const match = matchPath({ path: candidate.pattern, end: false }, pathname)
    const id = match?.params?.entityId
    if (!id) continue
    if (RESERVED_SEGMENTS.has(id.toLowerCase())) continue
    // All BP / Task / Action / Incident / Unit / Member IDs are UUIDs. Skip
    // values that aren't — protects against future sub-routes leaking in.
    if (!UUID_RE.test(id)) continue
    return { entityType: candidate.entityType, entityId: id }
  }
  return { entityType: null, entityId: null }
}

export function useGuruContext() {
  const location = useLocation()
  const bp = useSelector(getSelectedBp)

  // 1) Direct BP / portfolio route (canonical)
  for (const route of ROUTES) {
    const match = matchPath({ path: route.pattern, end: false }, location.pathname)
    if (match) {
      const folder = route.folderParam ? match.params[route.folderParam] : null
      const subSurface = folder && KNOWN_BP_FOLDERS.has(folder) ? folder : null
      const entity = detectEntity(location.pathname)

      return {
        contextType: route.contextType,
        contextId: match.params[route.idParam],
        contextLabel: route.contextType === 'bp' ? bp?.name || null : null,
        subSurface,
        entityType: entity.entityType,
        entityId: entity.entityId,
      }
    }
  }

  // 2) Hub pages (/tasks, /actions, /incidents, /calendar): scope comes from
  //    ?bp=<uuid> or ?portfolio=<uuid>. Sub-surface comes from the route's
  //    surfacePrefix plus an optional ?tab=… qualifier.
  for (const hub of HUB_ROUTES) {
    if (matchPath({ path: hub.pattern, end: false }, location.pathname)) {
      const params = new URLSearchParams(location.search || '')
      const bpId = params.get('bp')
      const portfolioId = params.get('portfolio')
      const tab = params.get('tab')
      const entity = detectEntity(location.pathname)

      let subSurface = hub.surfacePrefix
      if (tab && KNOWN_HUB_TABS.has(tab) && tab !== hub.surfacePrefix) {
        subSurface = tab // e.g. /tasks?tab=compliance → 'compliance'
      }

      if (bpId && UUID_RE.test(bpId)) {
        return {
          contextType: 'bp',
          contextId: bpId,
          contextLabel: bp?.id === bpId ? bp?.name || null : null,
          subSurface,
          entityType: entity.entityType,
          entityId: entity.entityId,
        }
      }
      if (portfolioId && UUID_RE.test(portfolioId)) {
        return {
          contextType: 'portfolio',
          contextId: portfolioId,
          contextLabel: null,
          subSurface,
          entityType: entity.entityType,
          entityId: entity.entityId,
        }
      }
      // Hub page without a specific scope — "global / across my buildings".
      // Backend supports context_type='user' to aggregate across every BP
      // the user can access.
      return {
        contextType: 'user',
        contextId: null,
        contextLabel: null,
        subSurface,
        entityType: entity.entityType,
        entityId: entity.entityId,
      }
    }
  }

  // /bp-list and /dashboard fall back to user-global too.
  if (location.pathname === '/bp-list' || location.pathname.startsWith('/bp-list?')
      || location.pathname === '/dashboard') {
    return {
      contextType: 'user',
      contextId: null,
      contextLabel: null,
      subSurface: location.pathname === '/dashboard' ? 'dashboard' : 'bp-list',
      entityType: null,
      entityId: null,
    }
  }

  const entity = detectEntity(location.pathname)
  return {
    contextType: null,
    contextId: null,
    contextLabel: null,
    subSurface: null,
    entityType: entity.entityType,
    entityId: entity.entityId,
  }
}
